import { useTranslate } from 'react-admin';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';

import { useResponsive } from 'src/hooks/use-responsive';

import { OFFICIAL_VAULTS_NS as NS } from './constants';
import type { ApplicationView } from './helpers';
import type { RedriveForm } from './useOfficialVaults';

/**
 * Resume a FAILED official request from the step that failed (XBIT-4240, C-08 v3.1). It keeps the vault id and the terms,
 * so the dialog names the failure the operator is acting on and says what this does NOT do: a failure caused by the terms
 * fails again, and the Retry beside it (a new request under a fresh id) is the way to change them. A refusal keeps the
 * dialog and its reason; nothing was written.
 */
export const RedriveDialog = ({
  form,
  request = null,
}: {
  form: Pick<RedriveForm, 'vaultId' | 'alert' | 'submitting' | 'closeDialog' | 'submit'>;
  /** The FAILED request being resumed, for its failed step and reason. */
  request?: Pick<ApplicationView, 'failure'> | null;
}) => {
  const translate = useTranslate();
  const smDown = useResponsive('down', 'sm');

  return (
    <Dialog
      open={form.vaultId !== null}
      onClose={form.submitting ? undefined : form.closeDialog}
      maxWidth="sm"
      fullWidth
      fullScreen={smDown}
    >
      <DialogTitle>{translate(`${NS}.redrive.title`, { vaultId: form.vaultId })}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {request?.failure && (
            <Alert severity="error">
              {translate(`${NS}.table.failure`, {
                step: request.failure.step,
                reason: request.failure.reason,
              })}
            </Alert>
          )}
          <Typography variant="body2">{translate(`${NS}.redrive.what`)}</Typography>
          <Alert severity="warning">{translate(`${NS}.redrive.termsNotice`)}</Alert>
          {form.alert && (
            <Alert severity={form.alert.severity}>
              <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2 }}>
                {form.alert.messages.map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </Stack>
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={form.closeDialog} disabled={form.submitting}>
          {translate(`${NS}.form.cancel`)}
        </Button>
        <LoadingButton
          variant="contained"
          onClick={form.submit}
          disabled={form.submitting}
          loading={form.submitting}
        >
          {translate(`${NS}.redrive.action`)}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
